import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import ArchiveCard from "@/components/feature/ArchiveCard";
import { archives, categories, hotArchive } from "@/mocks/archive";
import { usePageSeo } from "@/hooks/usePageSeo";

type Sort = "hot" | "new" | "views";

const catMeta: Record<string, { title: string; desc: string }> = {
  "yan-yi": { title: "演艺新星档案｜影视新面孔破圈记录 - 新瓜档案", desc: "影视剧综冒头的新晋艺人与新面孔瓜，跟踪他们的破圈轨迹与新兴人物动态。" },
  "duan-shi": { title: "短剧新秀档案｜竖屏爆款新面孔合集 - 新瓜档案", desc: "竖屏短剧里眼熟的新晋人物与新人黑料争议梳理，一次认清所有短剧新面孔。" },
  "xuan-xiu": { title: "选秀回锅档案｜翻红新人热议 - 新瓜档案", desc: "二次登台重获关注的选秀新人档案，聚焦新兴人物与新爆料主角的翻红之路。" },
  "zhu-bo": { title: "直播新锐档案｜从直播间走红的新面孔 - 新瓜档案", desc: "靠才艺与硬核内容走红的直播新锐人物合集，最新吃瓜的新面孔都在这里。" },
  "da-huo": { title: "带货新势力档案｜边卖边火的新主播 - 新瓜档案", desc: "话题度极高的新晋带货主播与新兴人物档案，一次认清所有带货新面孔。" },
};

export default function Archives() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "all";
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<Sort>("hot");

  const currentMeta = activeCat !== "all" ? catMeta[activeCat] : null;

  usePageSeo(
    currentMeta?.title || "人物档案库｜新晋人物 · 新面孔瓜合集 - 新瓜档案",
    currentMeta?.desc || "新瓜档案人物档案库：收录演艺新星、短剧新秀、选秀回锅、直播新锐与带货新势力等新晋人物，一次认清所有新面孔瓜主角。"
  );

  const list = useMemo(() => {
    let out = [...archives];
    if (activeCat !== "all") out = out.filter((a) => a.category === activeCat);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.role.toLowerCase().includes(q) ||
          a.tag.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q)
      );
    }
    if (sort === "hot") out.sort((a, b) => b.heat - a.heat);
    if (sort === "new") out.sort((a, b) => a.day.localeCompare(b.day));
    if (sort === "views") out.sort((a, b) => b.views - a.views);
    return out;
  }, [activeCat, query, sort]);

  const categoryName = activeCat !== "all" ? categories.find((c) => c.slug === activeCat)?.name || "全部" : "全部";

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Header />

      <main>
        {/* 页头 */}
        <section className="relative overflow-hidden border-b border-background-200 bg-background-100/70">
          <div className="container-x py-12">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-600">人物档案库</span>
            </nav>
            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">Archive Library</p>
                <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                  新面孔人物档案库 <span className="align-middle text-lg font-bold text-accent-500">· {categoryName}</span>
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-500">
                  收录最新吃瓜的新晋人物、新爆料主角与新兴人物档案，支持按栏目筛选、关键词搜索与热度排序，一次认清所有新瓜主角。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 筛选区 */}
        <section className="border-b border-background-200 bg-background-100/60 py-4">
          <div className="container-x flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setParams({})}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  activeCat === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                全部
              </button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setParams({ cat: c.slug })}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    activeCat === c.slug ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索新面孔姓名 / 栏目"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 sm:w-56"
                />
                {query && (
                  <button type="button" onClick={() => setQuery("")} aria-label="清除搜索" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-primary-600">
                    <i className="ri-close-circle-fill" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 rounded-md bg-background-100 p-1">
                {([
                  ["hot", "热度", "ri-fire-fill"],
                  ["new", "最新", "ri-time-line"],
                  ["views", "浏览", "ri-eye-line"],
                ] as [Sort, string, string][]).map(([k, label, icon]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setSort(k)}
                    className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      sort === k ? "bg-background-50 text-primary-600 shadow-sm ring-1 ring-background-200" : "text-foreground-500"
                    }`}
                  >
                    <i className={icon} /> {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 结果区 */}
        <section className="container-x py-10 md:py-12">
          {list.length ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {list.map((it) => (
                  <ArchiveCard key={it.slug} item={it} />
                ))}
              </div>
              <p className="mt-8 text-center text-xs text-foreground-400">当前栏目共收录 {list.length} 位新面孔档案</p>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg bg-background-100 py-20 text-center">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
              <p className="mt-4 font-heading text-lg font-bold text-foreground-800">没有找到相关的新面孔档案</p>
              <p className="mt-2 text-sm text-foreground-500">试试切换栏目或更换关键词</p>
              <button
                type="button"
                onClick={() => { setQuery(""); setParams({}); }}
                className="btn mt-6 bg-primary-500 text-background-50 hover:bg-primary-600"
              >
                清除筛选 <i className="ri-restart-line" />
              </button>
            </div>
          )}
        </section>

        {/* 热门横向 */}
        <section className="border-t border-background-200 bg-background-100/60">
          <div className="container-x py-12">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-fire-fill text-accent-500" /> 本周最热新面孔
            </h2>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              {hotArchive.slice(0, 3).map((it, i) => (
                <div key={it.slug} className="flex items-center gap-3 rounded-lg bg-background-50 p-3 ring-1 ring-background-200">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-500 font-heading text-lg font-black text-background-50">{i + 1}</span>
                  <Link to={`/archives/${it.slug}`} className="block flex-1 truncate text-sm font-semibold text-foreground-800 hover:text-primary-600">
                    {it.name} · {it.role}
                  </Link>
                  <span className="flex shrink-0 items-center gap-1 text-xs font-bold text-primary-600"><i className="ri-fire-fill" /> {it.heat}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}